import { createContext, useContext, useEffect, useRef } from "react";
import i18n from "i18next";
import type { ReactNode } from "react";
import Tracking from "@/lib/tracking";
import { getStoredFunnelFlow } from "@/lib/funnelFlow";
import { DEFAULT_LOCALE } from "@/lib/i18n/locales";
import { runWhenIdle } from "@/lib/runWhenIdle";
import { getUTMParams } from "@/lib/utmTracking";
import { AmplitudeTrackingEvent } from "@/types/amplitude-tracking.type";

/**
 * Context for tracking manager
 */
const TrackingContext = createContext<Tracking | null>(null);

/**
 * Props for TrackingProvider component
 */
export interface TrackingProviderProps {
  children: ReactNode;
}

/**
 * Provider component that initializes tracking and provides context
 */
export function TrackingProvider({ children }: TrackingProviderProps) {
  const managerRef = useRef<Tracking | null>(null);

  // Initialize manager once
  if (!managerRef.current) {
    managerRef.current = new Tracking();
  }

  // Initialize after the page has finished loading and the browser is idle.
  // Why: amplitude.init() POSTs to api2.amplitude.com on the LCP critical path
  // (audited at 1.8–2.7s). Anything user-triggered before init runs will lazily
  // boot tracking via Tracking.init() inside the track* methods — which is why
  // the session start event is sent from here, not from the root's boot, where
  // it would pull the whole init onto the critical path (SHO-852).
  useEffect(() => {
    const manager = managerRef.current;
    if (!manager) return;

    const start = () => {
      manager.setFlowType(getStoredFunnelFlow());
      manager.setAppLocale(i18n.resolvedLanguage ?? DEFAULT_LOCALE);
      manager.init();
      manager.trackAmplitudeEvent(
        AmplitudeTrackingEvent.customSessionStart,
        getUTMParams(),
      );
    };

    const boot = () => runWhenIdle(start);

    if (document.readyState === "complete") {
      boot();
    } else {
      window.addEventListener("load", boot, { once: true });
      return () => window.removeEventListener("load", boot);
    }
  }, []);

  // Keep the user property in sync with runtime language switches. Subscribing
  // to i18next rather than to `useApp` for the usual reason: it holds the
  // *resolved* locale, and it means the store doesn't need a handle on the
  // manager, which only lives in this context. Not `useTranslation()` — that
  // would re-render this provider on every switch for a side effect.
  useEffect(() => {
    const manager = managerRef.current;
    if (!manager) return;

    const onLanguageChanged = (locale: string) => manager.setAppLocale(locale);
    i18n.on("languageChanged", onLanguageChanged);
    return () => i18n.off("languageChanged", onLanguageChanged);
  }, []);

  return (
    <TrackingContext.Provider value={managerRef.current}>
      {children}
    </TrackingContext.Provider>
  );
}

/**
 * Hook to access the tracking manager
 */
export function useTracking(): Tracking {
  const manager = useContext(TrackingContext);

  if (!manager) {
    throw new Error(
      "useTracking must be used within a TrackingProvider component",
    );
  }

  return manager;
}
